/**
 * houseSeed.js — seed the Projection Lab wizard from the house projections.
 *
 * A team the user has never touched used to open on `buildInit*`: league-average
 * slot shares blended with the player's own history. That is a reasonable cold
 * start, but it is NOT what the rest of the app now shows for that team — the
 * board, the rankings and My Value all read DEFAULT_PROJECTIONS. So a user who
 * opened the wizard on an unedited team saw the numbers move for no reason they
 * caused. These builders close that gap: an untouched team opens ON the house
 * projection, and every slider starts where the house number is.
 *
 * 🔴 buildInitAllocs / buildInitRates ARE NOT MODIFIED, and must not be.
 * `projectAllDefaults` calls them and the projlab golden test pins its output, so
 * changing their behaviour would silently move a pinned reference. These are
 * additive wrappers: they call the originals for the base, then OVERRIDE only the
 * slots the house set actually covers. Remainder buckets (RB3+/WR4+) and any slot
 * the house set lacks keep their existing seeds untouched, by construction.
 *
 * ── HOW THE INVERSION WORKS ────────────────────────────────────────────────
 * The forward pipeline (computeVolume) is:
 *     targets = round(teamPassAtt x tgtPct/100 x games/17)
 *     rushAtt = round(teamRushAtt x rushPct/100 x games/17)
 * so seeding `games` from the house row and setting
 *     tgtPct  = houseTargets x 17 x 100 / (teamPassAtt x houseGames)
 * makes the team total CANCEL — the reconstructed volume is the house volume
 * regardless of what step 2 says the team's totals are. That is why the shares
 * are computed at a 17-game pace and why this does not need step 2 to agree.
 *
 * Rates then invert trivially, because buildPlayerResult is a chain of products:
 *     rec = round(targets x catchPct/100)  ->  catchPct = houseRec / houseTargets
 *     recYds = round(rec x ypr)            ->  ypr      = houseRecYds / houseRec
 * and so on for recTDPct / ypc / rushTDPct.
 *
 * ── WHAT CANNOT BE INVERTED, AND WHY ───────────────────────────────────────
 * Documented rather than forced (see houseSeed.roundtrip.test.js for measured
 * deviations):
 *
 *   QB passAtt — computeVolume sets it to `round(teamPassAtt x games/17)`. There
 *     is no per-QB share knob to solve for, so it follows step 2's team total.
 *     The house module carries no totalPlays/passPct to restore step 2 from.
 *
 *   QB comp / passYds / passTD — NOT computed from QB rates at all.
 *     `computeDerivedPass` sums the RECEIVERS' rec / recYds / recTD across every
 *     non-QB stint, including the RB3+/WR4+ remainder buckets. The house set drops
 *     those buckets, and this seeder leaves them on their legacy defaults, so the
 *     sum cannot land on the charted QB line. Measured: the charted QB passYds
 *     differs from the sum over the charted receivers by a mean of +235 and a
 *     range of -1644..+1144, i.e. the remainder rows carry a real share of it.
 *
 *   QB int — `round(passAtt x intPct/100)`, so it inherits passAtt's error above.
 *
 *   QB compPct / ypa / tdPct — set here for display honesty only. They are inert
 *     in the projection: calcPPR takes passing from derivedPass and never reads
 *     them (the dormant-TD-rate issue already recorded in CLAUDE.md).
 *
 *   A house row with no matching stint — the ARI QB1#1 split-season case.
 *     buildInitAllocs creates one stint per slot, so a second charted stint has
 *     nowhere to land until the user adds one. Left alone rather than invented.
 */

import {
  SLOTS,
  buildInitAllocs,
  buildInitRates,
  resolvedStintsFor,
} from "./projlabProjection";
import { DEFAULT_PROJECTIONS } from "../data/defaultProjections2026";

// Seed provenance codes, for the explain collector. SYNC PACT (CLAUDE.md):
// these must stay in step with RATE_SEED_TAGS in TeamEditor.jsx — same commit.
export const HOUSE_ALLOC_CODE = "house";
export const HOUSE_RATE_CODE = "house";

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
// Guarded ratio: a zero denominator means the house line has no volume of that
// kind, and the right answer is "leave the rate at its seed", not Infinity/NaN.
const ratio = (a, b) => (num(b) > 0 ? num(a) / num(b) : null);
const pctOf = (a, b) => { const r = ratio(a, b); return r === null ? null : r * 100; };

/** House rows for a team, keyed by STINT id. Row slot "WR1" is stint "WR1#0";
 *  a 2nd+ stint already carries its own id ("QB1#1"), matching how
 *  projectTeamPlayers labels them. */
export function houseRowsByStint(abbrev) {
  const map = new Map();
  for (const r of DEFAULT_PROJECTIONS[abbrev] || []) {
    const slot = String(r.slot || "");
    if (!slot) continue;
    map.set(slot.includes("#") ? slot : `${slot}#0`, r);
  }
  return map;
}

/** True when the house set can seed this team at all. */
export function hasHouseRows(abbrev) {
  return (DEFAULT_PROJECTIONS[abbrev] || []).length > 0;
}

/**
 * THE house-seed decision, owned here rather than inline in TeamEditor so it is
 * testable without rendering the wizard.
 *
 * Keys on CONFIRMED WORK, not on the existence of a save file. A save gets written
 * as soon as the user opens the wizard and nudges step 2, so gating on `savedInit`
 * meant a save carrying no allocation or efficiency work still suppressed the house
 * seed — the user got league-average seeds for having merely looked at the team.
 *
 * PER-LAYER, and the two really can diverge:
 *   allocs -> gated on `allocSummary`. Where that exists, `initialAllocs`
 *     (TeamEditor:3602) already wins for state, and the seed is additionally the
 *     reference for freshAllocs / seedSlots / lockedSlots — so gating here leaves
 *     every one of those byte-identical for a user with real alloc work.
 *   rates  -> gated on `effSummary` (TeamEditor:3656), same reasoning, plus the
 *     documented rebuild merge `...built, ...playerEdit, ...initialRates`.
 *
 * The interesting case is allocSummary present / effSummary null: the user has
 * confirmed their own allocation but not their efficiency. House rates compose
 * correctly on top of the user's allocs because they are pure RATIOS
 * (catchPct = houseRec / houseTargets), not volumes — they say "this player catches
 * 68% of what he is thrown", which stays meaningful against any target share. A
 * stint the house set lacks falls through `rows.get(st.id)` in buildHouseRates and
 * keeps its buildInit seed.
 *
 * `savedInit` is the raw `safeReadProjlab` value for this team (null when there is
 * no save). Consumers still take a plain boolean, so callers pass `.allocs` /
 * `.rates`.
 */
export function houseSeedFlags(savedInit, abbrev) {
  const house = hasHouseRows(abbrev);
  return {
    allocs: !savedInit?.allocSummary && house,
    rates: !savedInit?.effSummary && house,
  };
}

/**
 * Allocation seeds from the house projection.
 * `teamTotals` is step 2's output: { passAtt, rushAtt }. It is the DENOMINATOR
 * only — it cancels out of the reconstruction (see header).
 */
export function buildHouseAllocs(abbrev, teamStarters, teamTotals, explain) {
  const init = buildInitAllocs(abbrev, teamStarters, explain);
  const rows = houseRowsByStint(abbrev);
  if (!rows.size) return init;

  const passAtt = num(teamTotals?.passAtt);
  const rushAtt = num(teamTotals?.rushAtt);

  for (const s of SLOTS) {
    // Remainder buckets keep their fixed defaults — the house set has no row for
    // them by construction, and inventing one would double-count the starters.
    if (s.isRemainder) continue;
    const slot = init[s.key];
    if (!slot) continue;

    const stints = resolvedStintsFor(s.key, init, teamStarters);
    const matched = stints
      .map((st) => ({ st, row: rows.get(st.id) }))
      .filter((m) => m.row);
    if (!matched.length) continue;

    // Games come straight from the house row — they are half of the inversion —
    // and so does IDENTITY. Binding a house row to a slot without adopting its
    // name left the slot showing the DEPTH CHART's player wearing another
    // player's projection: 66 rows across 25 of 32 teams, e.g. LA WR1 showing
    // Davante Adams with Puka Nacua's 170 targets. resolveStintIdentity reads
    // `st?.player ?? slotData?.player`, so setting it here is all the wiring the
    // wizard, the player card and the seed captions need. STARTERS_2025 is left
    // alone — the depth chart is unchanged and the user can still re-pick.
    for (const { st, row } of matched) {
      const target = slot.stints?.find((x) => x.id === st.id);
      if (!target) continue;
      if (num(row.games) > 0) target.games = row.games;
      if (row.player) {
        target.player = row.player;
        // 🔴 EMPTY STRING, NOT null. The id here is the DEPTH CHART player's, and
        // pairing it with the house player's name would resolve the wrong career
        // history. `null` does NOT suppress it: resolveStintIdentity uses
        // `st?.sleeperId ?? slotData?.sleeper_id`, and ?? treats null as nullish,
        // so it falls straight back to the depth chart's id. "" is non-nullish so
        // ?? keeps it, and every consumer of a stint id is a truthy check
        // (`sleeperId && ...` in findPlayerHist, `st.sleeperId ? ... : {}` for
        // per-player edits) — so "" reads as "no id" everywhere it is used while
        // actually blocking the fall-through. Lookup then falls to the name, which
        // is what we want.
        target.sleeperId = "";
      }
    }

    // Shares are computed on the slot TOTAL at a 17-game pace, because
    // computeVolume applies one slot-level tgtPct across all of the slot's
    // stints. With one stint (every non-QB slot in the house set) this is exact.
    const totalGames = matched.reduce((n, m) => n + num(m.row.games), 0);
    if (totalGames <= 0) continue;
    const pace = 17 / totalGames;

    const houseTargets = matched.reduce((n, m) => n + num(m.row.stats?.targets), 0);
    const houseCarries = matched.reduce(
      (n, m) => n + num(m.row.stats?.rushAtt ?? m.row.stats?.rush), 0);

    // Stored at 1dp, matching what buildInitAllocs already writes. The raw
    // quotient is a full-precision float, and it is the STORED value that the
    // alloc table and the slider boxes render — so an unrounded share showed up
    // as 19.666295884315904%. This is the fix for that; the display sites are
    // not the defect and get no formatter.
    //
    // Safe for the exact round-trip, and the roundtrip suite is the gate rather
    // than this comment: the reconstructed volumes are integers by construction,
    // and rounding the share perturbs them by at most base x 0.0005 (~0.31 at the
    // largest passAtt) against a 0.5 tolerance, so it cannot cross a boundary.
    if (passAtt > 0 && s.pos !== "QB") {
      slot.tgtPct = Math.round((houseTargets * pace) / passAtt * 100 * 10) / 10;
    }
    if (rushAtt > 0) {
      slot.rushPct = Math.round((houseCarries * pace) / rushAtt * 100 * 10) / 10;
    }
    if (explain?.slots?.[s.key]) {
      explain.slots[s.key].tgt = { code: HOUSE_ALLOC_CODE };
      explain.slots[s.key].rush = { code: HOUSE_ALLOC_CODE };
      explain.slots[s.key].games = { code: HOUSE_ALLOC_CODE };
    }
  }
  return init;
}

/** Efficiency seeds from the house projection. Every rate is house stat / house
 *  volume; a rate whose denominator is zero keeps its existing seed. */
export function buildHouseRates(abbrev, allocs, teamStarters, explain) {
  const init = buildInitRates(abbrev, allocs, teamStarters, explain);
  const rows = houseRowsByStint(abbrev);
  if (!rows.size) return init;

  for (const s of SLOTS) {
    if (s.isRemainder) continue;
    for (const st of resolvedStintsFor(s.key, allocs, teamStarters)) {
      const row = rows.get(st.id);
      if (!row) continue;
      const st8 = row.stats || {};
      const base = init[st.id] || {};
      const set = (k, v) => { if (v !== null && Number.isFinite(v)) base[k] = Math.round(v * 1e6) / 1e6; };

      if (s.pos === "QB") {
        // Display-only trio — inert in the projection (see header).
        set("compPct", pctOf(st8.comp, st8.passAtt));
        set("ypa", ratio(st8.passYds, st8.passAtt));
        set("tdPct", pctOf(st8.passTD, st8.passAtt));
        // These three DO drive output.
        set("intPct", pctOf(st8.int, st8.passAtt));
        set("rushYPC", ratio(st8.rushYds, st8.rushAtt));
        set("rushTDPct", pctOf(st8.rushTD, st8.rushAtt));
      } else if (s.pos === "RB") {
        set("ypc", ratio(st8.rushYds, st8.rushAtt));
        set("rushTDPct", pctOf(st8.rushTD, st8.rushAtt));
        set("catchPct", pctOf(st8.rec, st8.targets));
        set("ypr", ratio(st8.recYds, st8.rec));
        set("recTDPct", pctOf(st8.recTD, st8.rec));
      } else {
        set("catchPct", pctOf(st8.rec, st8.targets));
        set("ypr", ratio(st8.recYds, st8.rec));
        set("recTDPct", pctOf(st8.recTD, st8.rec));
      }
      init[st.id] = base;
      if (explain?.rates) explain.rates[st.id] = { all: HOUSE_RATE_CODE };
    }
  }
  return init;
}
